import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Card, Grid, Box, Stack, Typography, LinearProgress, CircularProgress, Collapse, IconButton, Tooltip } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { styled, useTheme } from "@mui/material/styles";
import { BaseChart } from "src/components/charts/BaseOptionChart";
import {
  calculateEnvironmentalMetrics,
  buildEnvironmentalTimeSeries,
} from "src/utils/carbonFootprint";

// Redesigned (V2) environmental-impact section — clean stat tiles + a CO2 trend,
// reusing the shared carbon-footprint calculations.

const Tile = styled(Box, {
  shouldForwardProp: (prop) => prop !== "accent",
})(({ theme, accent }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadiusMd,
  border: `1px solid ${theme.palette.grey[200]}`,
  borderLeft: `4px solid ${accent}`,
  background: theme.palette.background.paper,
  height: "100%",
}));

const toArray = (transactionsData) => {
  if (!transactionsData) return [];
  if (Array.isArray(transactionsData)) return transactionsData;
  if (typeof transactionsData === "object") return Object.values(transactionsData);
  return [];
};

const fmtKg = (v) => {
  const n = Number(v) || 0;
  return n >= 1000 ? `${(n / 1000).toLocaleString("en-IN", { maximumFractionDigits: 1 })} t` : `${Math.round(n)} kg`;
};
const fmtNum = (v) => Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const EnvironmentalImpactV2 = ({
  timeInterval,
  transactionsData,
  isLoading = false,
  carbonConstants,
  headerAction = null,
}) => {
  const theme = useTheme();
  const [chartOpen, setChartOpen] = useState(false);
  const txns = useMemo(() => toArray(transactionsData), [transactionsData]);

  const metrics = useMemo(
    () => calculateEnvironmentalMetrics(txns, carbonConstants),
    [txns, carbonConstants]
  );

  const series = useMemo(() => {
    if (!txns.length || !timeInterval) return { data: [], labels: [] };
    return buildEnvironmentalTimeSeries(txns, timeInterval, carbonConstants);
  }, [txns, timeInterval, carbonConstants]);

  // Drop each series' own `color` so the chart's high-contrast palette (above)
  // applies; keep name/type/data.
  const chartSeries = useMemo(
    () =>
      Array.isArray(series.data)
        ? series.data.map(({ color, ...rest }) => rest) // eslint-disable-line no-unused-vars
        : [],
    [series.data]
  );

  const chartOptions = useMemo(
    () => ({
      chart: {
        // The series are multi-metric lines (buildEnvironmentalTimeSeries sets
        // type: "line" on each). The chart type MUST match, or the combo renders
        // nothing — matches the working classic EnvironmentalImpact chart.
        type: "line",
        toolbar: { show: false },
        zoom: { enabled: false },
        fontFamily:
          'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      },
      // Theme chart palette so colors are consistent with the other dashboard
      // charts: red = emissions, green = trees, blue = offset. A solid fill keeps
      // the strokes opaque (the old gradient fill rendered the lines faint).
      colors: [
        theme.palette.chart.red[0],
        theme.palette.chart.green[0],
        theme.palette.chart.blue[0],
      ],
      stroke: { curve: "smooth", width: 3 },
      fill: { type: "solid", opacity: 1 },
      markers: { size: 3, strokeWidth: 0, hover: { size: 5 } },
      dataLabels: { enabled: false },
      legend: { position: "top", horizontalAlign: "left", fontSize: "12px" },
      grid: { borderColor: theme.palette.divider, strokeDashArray: 3, xaxis: { lines: { show: false } } },
      xaxis: {
        categories: series.labels || [],
        labels: { rotate: (series.labels || []).length > 12 ? -45 : 0, style: { fontSize: "11px", colors: theme.palette.text.secondary } },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: { labels: { style: { fontSize: "11px", colors: theme.palette.text.secondary }, formatter: (v) => Math.round(v).toLocaleString() } },
      tooltip: { y: { formatter: (v) => `${Math.round(v).toLocaleString()} kg` } },
    }),
    [series.labels, theme]
  );

  // With no charging in the period the score and reduction are just defaults;
  // show "—" rather than numbers that look measured.
  const hasData = Number(metrics.totalCO2Emissions) > 0 || Number(metrics.carbonOffset) > 0;

  const tiles = [
    { label: "CO₂ Emissions", value: fmtKg(metrics.totalCO2Emissions), accent: theme.palette.error.main },
    { label: "CO₂ Avoided", value: fmtKg(metrics.carbonOffset), accent: theme.palette.success.main, hint: "vs grid-only baseline" },
    { label: "Trees Equivalent", value: fmtNum(metrics.treesEquivalent), accent: theme.palette.success.dark },
    { label: "Sustainability", value: hasData ? `${Number(metrics.sustainabilityScore || 0).toFixed(1)}/10` : "—", accent: theme.palette.info.main },
  ];

  const reduction = Math.max(0, Math.min(100, Number(metrics.carbonFootprintReduction) || 0));

  return (
    <Card
      sx={{
        p: { xs: 2, sm: 2.5, md: 3 },
        borderRadius: 2,
        boxShadow: (t) => t.customShadows.z4,
        border: (t) => `1px solid ${t.palette.grey[200]}`,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Environmental Impact
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Carbon savings & sustainability
          </Typography>
        </Box>
        {headerAction}
      </Stack>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 280 }}>
          <CircularProgress size={36} />
        </Box>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {tiles.map((t) => (
              <Grid item xs={6} md={3} key={t.label}>
                <Tile accent={t.accent}>
                  <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 500 }}>
                    {t.label}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: t.accent }}>
                    {t.value}
                  </Typography>
                  {t.hint && (
                    <Typography variant="caption" sx={{ color: "text.disabled" }}>
                      {t.hint}
                    </Typography>
                  )}
                </Tile>
              </Grid>
            ))}
          </Grid>

          {/* Carbon footprint reduction */}
          <Box sx={{ mb: 3 }}>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Carbon footprint reduction vs grid-only
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: theme.palette.success.main }}>
                {hasData ? `${reduction.toFixed(1)}%` : "—"}
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={hasData ? reduction : 0}
              sx={{ height: 8, borderRadius: 4, bgcolor: "grey.200", "& .MuiLinearProgress-bar": { bgcolor: theme.palette.success.main } }}
            />
            <Typography variant="caption" sx={{ color: "text.disabled" }}>
              Renewable mix {fmtNum(metrics.renewablePercentage)}% · Avg daily CO₂ {fmtKg(metrics.avgDailyCO2)}
            </Typography>
          </Box>

          {/* CO2 trend — collapsible */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mt: 1 }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              CO₂ Trend
            </Typography>
            <Tooltip title={chartOpen ? "Hide graph" : "Show graph"}>
              <IconButton
                size="small"
                onClick={() => setChartOpen((prev) => !prev)}
                aria-label={chartOpen ? "collapse graph" : "expand graph"}
              >
                {chartOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Tooltip>
          </Stack>
          <Collapse in={chartOpen} timeout="auto" unmountOnExit>
            <Box sx={{ width: "100%", height: { xs: 240, sm: 280, md: 300 }, mt: 0.5 }}>
              {series.labels && series.labels.length > 0 ? (
                <BaseChart options={chartOptions} series={chartSeries} type="line" height="100%" width="100%" />
              ) : (
                <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "text.secondary" }}>
                  No environmental data for the selected period
                </Box>
              )}
            </Box>
          </Collapse>
        </>
      )}
    </Card>
  );
};

EnvironmentalImpactV2.propTypes = {
  timeInterval: PropTypes.object,
  transactionsData: PropTypes.any,
  isLoading: PropTypes.bool,
  carbonConstants: PropTypes.object,
  headerAction: PropTypes.node,
};

export default EnvironmentalImpactV2;
